import CT from '@marketplace-ai/tags';
import { useMemo } from 'react';
import { EmptyState, FormDialog, TableCard } from '~/components';
import { BasicGrid } from '~/components/agGrid/Grid';
import { PROVIDER_PAGE_KEYS } from '~/routing/routes';
import { ClaimsForm } from './ClaimsForm';
import { createClaimsColumns } from './tableConstants';
import { CreateMalpracticeClaimFormValues, MalpracticeClaimFormValues } from './types';

const TAGS = CT.malpracticeClaims.table;

const ClaimsNoRowsOverlay = () => (
  <EmptyState
    pageKey={PROVIDER_PAGE_KEYS.malpractice}
    title="No claims yet"
    cyTag={TAGS.emptyState}
  />
);

interface Props {
  data: MalpracticeClaimFormValues[];
  form: Partial<MalpracticeClaimFormValues>;
  loading: boolean;
  create: boolean;
  edit: boolean;
  handleEdit: (id: number) => void;
  handleDelete: (id: number) => void;
  handleSort: (sortBy: string) => void;
  triggerAddModal: () => void;
  handleClose: () => void;
  addClaim: (values: CreateMalpracticeClaimFormValues) => void;
  editClaim: (values: MalpracticeClaimFormValues) => void;
}

export const ClaimsDetails = (props: Props) => {
  const { data, loading, create, edit, handleEdit, handleDelete, triggerAddModal, handleClose } =
    props;

  // Memoize column defs to keep a stable reference across renders — otherwise
  // ag-grid re-runs its column-diff pipeline every render and can flicker or
  // lose column state (widths, sort, filter).
  const colDefs = useMemo(
    () => createClaimsColumns({ handleEdit, handleDelete }),
    [handleEdit, handleDelete]
  );

  return (
    <>
      <TableCard
        mobilePageHeader
        title="Malpractice Claims"
        addLabel="Add Malpractice Claim"
        onAddClick={triggerAddModal}
        addBtnTag={TAGS.addBtn}
        layoutTag={CT.malpracticeClaims.layout}
      >
        <BasicGrid
          newRecordsTable="malpractice_claims"
          data={data}
          loading={loading}
          colDefs={colDefs}
          pagination={true}
          cyTag={TAGS.grid}
          noRowsOverlayComponent={ClaimsNoRowsOverlay}
        />
      </TableCard>
      <FormDialog
        title={`${edit ? 'Edit' : 'Add'} Malpractice Claim`}
        editing={edit}
        creating={create}
        onClose={handleClose}
      >
        <ClaimsForm {...props} />
      </FormDialog>
    </>
  );
};
